import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, CircleStop, Eye, Play, RotateCcw, ShieldCheck, Tag as TagIcon, UserRoundCog, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function TicketActions({ t, onChanged, noteCount = 0, notesViewed = true, onViewNotes }) {
  const { user, can, readOnly } = useAuth();
  const [dlg, setDlg] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });

  const act = async (path, body = {}, msg) => {
    setBusy(true);
    try {
      await api.post(`/tickets/${t.id}/${path}`, body);
      if (msg) toast.success(msg);
      onChanged();
      setDlg(null);
      setNote("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (readOnly) return <div className="mt-3 text-[11px] text-amber-500" data-testid="readonly-notice">Read-only: exit view-as mode to act on this ticket.</div>;
  const mine = t.active_session?.user_id === user.id;
  const closed = t.status === "closed";
  const pending = t.status === "pending_approval";
  const mustReadNotes = noteCount > 0 && !notesViewed;
  const openApprove = () => {
    if (mustReadNotes) {
      toast.warning(`Read the ${noteCount} internal note${noteCount === 1 ? "" : "s"} on this ticket before approving its closure.`);
      onViewNotes?.();
      return;
    }
    setDlg("approve");
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ticket-actions">
      {can("tickets.work") && !closed && !pending && !mine && (
        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => setDlg("start")} data-testid="start-working-btn"><Play className="mr-1.5 h-3.5 w-3.5" /> Start working</Button>
      )}
      {can("tickets.pause") && t.active_session && (mine || can("tickets.assign")) && (
        <Button size="sm" variant="outline" className="h-8" onClick={() => act("pause-work", {}, "Work paused — stopwatch stopped, content locked again")} title={mine ? "Pause your work" : `Pause ${t.active_session.user_name}'s work`} data-testid="pause-working-btn"><CircleStop className="mr-1.5 h-3.5 w-3.5" /> Pause</Button>
      )}
      {can("tickets.close") && !closed && !pending && (
        <Button size="sm" variant="outline" className="h-8" onClick={() => setDlg("close")} data-testid="close-ticket-btn"><Check className="mr-1.5 h-3.5 w-3.5" /> {t.closure_requires_approval ? "Request closure" : "Close"}</Button>
      )}
      {pending && t.can_approve && (
        <>
          <Button size="sm" className={`h-8 ${mustReadNotes ? "bg-purple-600/50 hover:bg-purple-600/60" : "bg-purple-600 hover:bg-purple-700"}`} onClick={openApprove} title={mustReadNotes ? `Review the ${noteCount} internal note(s) first` : undefined} data-testid="approve-closure-btn">
            {mustReadNotes ? <Eye className="mr-1.5 h-3.5 w-3.5" /> : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />} {mustReadNotes ? `Read ${noteCount} note${noteCount === 1 ? "" : "s"} to approve` : "Approve closure"}
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => setDlg("reject")} data-testid="reject-closure-btn"><X className="mr-1.5 h-3.5 w-3.5" /> Reject</Button>
        </>
      )}
      {closed && can("tickets.reopen") && (
        <Button size="sm" variant="outline" className="h-8" onClick={() => act("reopen", {}, "Ticket reopened")} data-testid="reopen-ticket-btn"><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reopen</Button>
      )}
      {can("tickets.assign") && !closed && (
        <Select value={t.assignee_id || "unassigned"} onValueChange={(v) => act("assign", { user_id: v === "unassigned" ? null : v }, "Assignee updated")}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="assign-select"><UserRoundCog className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" /><SelectValue /></SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
            {users.filter((u) => u.active && !u.view_only).map((u) => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {can("tickets.work", "tickets.assign") && !closed && (
        <Button size="sm" variant="ghost" className="h-8" onClick={() => setDlg("tag")} data-testid="add-tag-btn"><TagIcon className="mr-1.5 h-3.5 w-3.5" /> Tag</Button>
      )}

      <AlertDialog open={dlg === "start"} onOpenChange={(o) => !o && setDlg(null)}>
        <AlertDialogContent data-testid="start-working-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start working {t.number}?</AlertDialogTitle>
            <AlertDialogDescription>
              Working this ticket starts the stopwatch and SLA tracking, assigns it to you{t.active_session ? ` and takes it over from ${t.active_session.user_name}` : ""}, and reveals the email content. Any ticket you are currently working will be paused.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="start-working-cancel-btn">Not now</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("start-work", {}, `You are now working ${t.number}. Stopwatch running.`)} data-testid="start-working-confirm-btn">Yes, start working</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={["close", "approve", "reject", "tag"].includes(dlg)} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid={`${dlg}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {dlg === "close" && (t.closure_requires_approval ? `Request closure of ${t.number}` : `Close ${t.number}`)}
              {dlg === "approve" && `Approve closure of ${t.number}`}
              {dlg === "reject" && `Reject closure of ${t.number}`}
              {dlg === "tag" && `Tag ${t.number}`}
            </DialogTitle>
            <DialogDescription>
              {dlg === "close" && t.closure_requires_approval && `A closure rule applies here. ${t.closure_approvers.join(", ")} must approve before the ticket closes.`}
              {dlg === "close" && !t.closure_requires_approval && "The ticket moves to the archive. It is never deleted and can be reopened."}
              {dlg === "approve" && `This closes the ticket on behalf of the requester and records your approval in the history.${noteCount ? ` You have reviewed the ${noteCount} internal note(s).` : ""}`}
              {dlg === "reject" && "The ticket goes back to open. Tell the requester why."}
              {dlg === "tag" && "Tags are visible in the list and recorded in the ticket history."}
            </DialogDescription>
          </DialogHeader>
          {dlg === "tag" ? (
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. URGENT" className="mono uppercase" data-testid="tag-input" />
          ) : (
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={dlg === "reject" ? "Reason (recommended)" : "Optional note for the history"} data-testid="action-note-input" />
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)}>Cancel</Button>
            {dlg === "close" && <Button disabled={busy} onClick={() => act("close", { reason: note }, t.closure_requires_approval ? "Closure requested — approvers notified" : "Ticket closed")} data-testid="close-confirm-btn">{t.closure_requires_approval ? "Request approval" : "Close ticket"}</Button>}
            {dlg === "approve" && <Button className="bg-purple-600 hover:bg-purple-700" disabled={busy} onClick={() => act("approve-closure", { reason: note, notes_reviewed: notesViewed || noteCount === 0 }, "Closure approved")} data-testid="approve-confirm-btn">Approve & close</Button>}
            {dlg === "reject" && <Button variant="destructive" disabled={busy} onClick={() => act("reject-closure", { reason: note }, "Closure rejected")} data-testid="reject-confirm-btn">Reject</Button>}
            {dlg === "tag" && <Button disabled={busy || !note.trim()} onClick={() => act("tags", { tag: note }, "Tag added")} data-testid="tag-confirm-btn">Add tag</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
